import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
} from '@mui/material';
import { Task } from '../types';
import { analyzeTask } from '../services/geminiService';

interface AddTaskProps {
  onAddTask: (task: Task) => void;
}

const AddTask: React.FC<AddTaskProps> = ({ onAddTask }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [quadrant, setQuadrant] = useState<number>(1);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    const newTask: Task = {
      id: Date.now().toString(),
      title,
      description,
      quadrant,
      completed: false,
      createdAt: new Date(),
    };

    onAddTask(newTask);
    setTitle('');
    setDescription('');
    setQuadrant(1);
  };

  const handleAnalyze = async () => {
    if (!description.trim() && !title.trim()) return;
    
    setIsAnalyzing(true);
    try {
      const taskContext = title.trim() 
        ? `${title.trim()}: ${description.trim()}`
        : description.trim();
      const suggestion = await analyzeTask(taskContext);
      setTitle(suggestion.title);
      setDescription(suggestion.description);
      setQuadrant(suggestion.quadrant);
    } catch (error) {
      console.error('Error analyzing task:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Task Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          margin="normal"
          multiline
          rows={2}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Quadrant</InputLabel>
          <Select
            value={quadrant}
            label="Quadrant"
            onChange={(e) => setQuadrant(Number(e.target.value))}
          >
            <MenuItem value={1}>Quadrant 1: Important & Urgent</MenuItem>
            <MenuItem value={2}>Quadrant 2: Important & Not Urgent</MenuItem>
            <MenuItem value={3}>Quadrant 3: Not Important & Urgent</MenuItem>
            <MenuItem value={4}>Quadrant 4: Not Important & Not Urgent</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            onClick={handleAnalyze}
            disabled={isAnalyzing || !description.trim()}
            sx={{ flex: 1 }}
          >
            {isAnalyzing ? <CircularProgress size={24} /> : 'Analyze with AI'}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ flex: 1 }}
          >
            Add Task
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default AddTask; 